import { Avatar, Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import ChevronRight from "@material-ui/icons/ChevronRight";
import React from "react";
import { useHistory } from "react-router";

import { ProposalMatching } from "@/api/generated";
import { homeCardStyle, HomeRail, HomeSection } from "@/components/home/HomeSection";
import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { buildAgentName } from "@/utilities/agentName";

import NoImage from "@assets/image/NoImage.svg";

// SP のカードは Figma（3ma7YITEPYEXiwmlWrWdoe の PlannerCard 2040:2829）に合わせる。
// 幅 158 の白カードに写真 64 を横中央、名前と会社名は幅 120 で左寄せ、右端に遷移の矢印
const SP_CARD_WIDTH = 158;
const SP_TEXT_WIDTH = 120;
// 矢印アイコンの枠は 20px だが、Figma の Chevron（6×10）と同じく矢印の幅だけをレイアウトに数える
const SP_ARROW_INSET = -7;

const useStyles = makeStyles({
  list: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  card: {
    ...homeCardStyle,
    padding: layoutTokens.space[16],
    gap: layoutTokens.space[12],
    textAlign: "left",
    display: "flex",
    width: "100%",
    justifyContent: "flex-start",
  },
  spCard: {
    width: SP_CARD_WIDTH,
    padding: `${layoutTokens.space[12]}px ${layoutTokens.space[8]}px ${layoutTokens.space[16]}px ${layoutTokens.space[12]}px`,
    gap: layoutTokens.space[4],
    alignItems: "center",
  },
  spBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: layoutTokens.space[12],
  },
  avatar: { width: 44, height: 44 },
  spAvatar: { width: 64, height: 64 },
  texts: { flex: 1, minWidth: 0 },
  spTexts: { width: SP_TEXT_WIDTH, maxWidth: "100%" },
  name: { ...textStyles["PC/Heading/H4"], color: semanticColors["text/primary"], overflowWrap: "anywhere" },
  company: {
    ...textStyles["PC/Caption"],
    color: semanticColors["text/secondary"],
    marginTop: layoutTokens.space[4],
    overflowWrap: "anywhere",
  },
  arrow: { color: semanticColors["icon/primary"], fontSize: 20, flexShrink: 0 },
  spArrow: { marginLeft: SP_ARROW_INSET, marginRight: SP_ARROW_INSET },
});

export const MatchingAgentSection: React.FC<{ proposalMatchings: ProposalMatching[] }> = ({ proposalMatchings }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const history = useHistory();
  const handleClick = (matching: ProposalMatching) => {
    if (matching.matching_type === "投資") history.push(`investment/${matching.investment_proposal_id}`);
    if (matching.matching_type === "査定") history.push(`assessment/${matching.assessment_proposal_id}`);
    if (matching.matching_type === "ユーザ" || matching.matching_type === "手動")
      history.push(`agent_search/${matching.agent_id}`);
  };
  if (!proposalMatchings.length) return null;
  const cards = proposalMatchings.map((matching) => {
    const avatar = (
      <Avatar
        className={isMobile ? classes.spAvatar : classes.avatar}
        src={matching.agent?.agent_profile_image?.image_url || NoImage}
        alt=""
      />
    );
    const texts = (
      <Box className={isMobile ? classes.spTexts : classes.texts}>
        <Typography className={classes.name}>{buildAgentName(matching.agent ?? {})}</Typography>
        <Typography className={classes.company}>{matching.agent?.company?.name}</Typography>
      </Box>
    );
    return (
      <ButtonBase
        key={matching.id}
        className={`${classes.card} ${isMobile ? classes.spCard : ""}`}
        onClick={() => handleClick(matching)}
      >
        {isMobile ? (
          // SP は写真の下に名前・会社名を縦に並べる（PC は従来どおり横並びのリスト）
          <Box className={classes.spBody}>
            {avatar}
            {texts}
          </Box>
        ) : (
          <>
            {avatar}
            {texts}
          </>
        )}
        <ChevronRight className={`${classes.arrow} ${isMobile ? classes.spArrow : ""}`} />
      </ButtonBase>
    );
  });
  return (
    <HomeSection title="マッチ中の認定プランナー">
      {isMobile ? (
        <HomeRail label="マッチ中の認定プランナー">{cards}</HomeRail>
      ) : (
        <Box className={classes.list}>{cards}</Box>
      )}
    </HomeSection>
  );
};
